import React, { useState, useEffect } from "react";
import { useData } from "../../context/DataContext";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../ui/dialog";
import { toast } from "sonner";
import { Edit, Plus, Save, Loader2, Archive, RotateCcw, AlertCircle, Users, Settings, ListChecks, Search, Tags, Bell, X, ShieldAlert } from "lucide-react";
import api from "../../utils/api";
import { QualificationType, QualificationGroup, QualificationDefault, User } from "../../types";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";

export default function QualificationSettings() {
  const { users, selectionUsers, fetchSelectionUsers } = useData();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState("presets");
  const [isLoading, setIsLoading] = useState(false);
  
  // Data states
  const [types, setTypes] = useState<QualificationType[]>([]);
  const [defaults, setDefaults] = useState<QualificationDefault | null>(null);
  const [groups, setGroups] = useState<QualificationGroup[]>([]);
  const [presets, setPresets] = useState<any[]>([]);
  
  // Group Dialog states
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<QualificationGroup | null>(null);
  const [groupLabel, setGroupLabel] = useState("");
  const [selectedGroupUserIds, setSelectedGroupUserIds] = useState<string[]>([]);
  const [groupSearchTerm, setGroupSearchTerm] = useState("");
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [isSavingGroup, setIsSavingGroup] = useState(false);
  
  // Type Dialog states
  const [isTypeDialogOpen, setIsTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<QualificationType | null>(null);
  const [typeLabel, setTypeLabel] = useState("");
  const [isSavingType, setIsSavingType] = useState(false);

  // Preset Dialog states
  const [isPresetDialogOpen, setIsPresetDialogOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<any | null>(null);
  const [presetName, setPresetName] = useState("");
  const [presetTypeId, setPresetTypeId] = useState("");
  const [presetAuthority, setPresetAuthority] = useState("");
  const [presetSearchTerm, setPresetSearchTerm] = useState("");
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [defaultsSearchTerm, setDefaultsSearchTerm] = useState("");
  const [isSavingPreset, setIsSavingPreset] = useState(false);
  
  const [isSavingDefaults, setIsSavingDefaults] = useState(false);
  const [defaultIntervalInput, setDefaultIntervalInput] = useState("");

  const ability = useAbility(AbilityContext);
  const canReadDefaults = ability.can(ACTIONS.READ, RESOURCES.QUALIFICATION_DEFAULT);
  const canReadGroups = ability.can(ACTIONS.READ, RESOURCES.QUALIFICATION_GROUP);
  const canReadTypes = ability.can(ACTIONS.READ, RESOURCES.QUALIFICATION_TYPE);
  const canReadPresets = ability.can(ACTIONS.READ, RESOURCES.QUALIFICATION_PRESET);
  const canReadSettings = ability.can(ACTIONS.READ, RESOURCES.QUALIFICATION_SETTINGS);
  
  const canUpdateDefaults = ability.can(ACTIONS.UPDATE, RESOURCES.QUALIFICATION_DEFAULT);
  const canUpdateGroups = ability.can(ACTIONS.UPDATE, RESOURCES.QUALIFICATION_GROUP);
  const canUpdateTypes = ability.can(ACTIONS.UPDATE, RESOURCES.QUALIFICATION_TYPE);
  const canUpdatePresets = ability.can(ACTIONS.UPDATE, RESOURCES.QUALIFICATION_PRESET);
  const canUpdateSettings = ability.can(ACTIONS.UPDATE, RESOURCES.QUALIFICATION_SETTINGS);

  useEffect(() => {
    if (activeTab === "defaults" && canReadDefaults) {
      if (canReadTypes) fetchTypes();
      fetchDefaults();
      fetchSelectionUsers();
    } else if (activeTab === "groups" && canReadGroups) {
      fetchGroups();
      fetchSelectionUsers();
    } else if (activeTab === "categories" && canReadTypes) {
      fetchTypes();
    } else if (activeTab === "presets" && canReadPresets) {
      if (canReadTypes) fetchTypes();
      fetchPresets();
    }
  }, [activeTab, canReadDefaults, canReadGroups, canReadTypes, canReadPresets]);

  const fetchPresets = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/api/qualifications/presets/all", { skipErrorToast: true });
      if (response.success) {
        setPresets(response.presets);
      }
    } catch (error) {
      console.error("Fetch presets error:", error);
      toast.error("Failed to load presets");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTypes = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/api/qualifications/types/all", { skipErrorToast: true });
      if (response.success) {
        setTypes(response.types);
      }
    } catch (error) {
      console.error("Fetch types error:", error);
      toast.error("Failed to load qualification types");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDefaults = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/api/qualifications/defaults", { skipErrorToast: true });
      if (response.success) {
        setDefaults(response.defaults);
      }
    } catch (error) {
      console.error("Fetch defaults error:", error);
      toast.error("Failed to load notification defaults");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGroups = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/api/qualifications/groups/all", { skipErrorToast: true });
      if (response.success) {
        setGroups(response.groups);
      }
    } catch (error) {
      console.error("Fetch groups error:", error);
      toast.error("Failed to load qualification groups");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveDefaults = async () => {
    if (!defaults) return;

    setIsSavingDefaults(true);
    try {
      const response = await api.put("/api/qualifications/defaults", {
        defaultRecipients: defaults.defaultRecipients,
        defaultIntervals: defaults.defaultIntervals
      }, { skipErrorToast: true });
      if (response.success) {
        setDefaults(response.defaults);
        toast.success("Notification defaults saved");
      }
    } catch (error) {
      console.error("Save defaults error:", error);
      toast.error("Failed to save defaults");
    } finally {
      setIsSavingDefaults(false);
    }
  };

  const handleOpenGroupDialog = (group?: QualificationGroup) => {
    if (group) {
      setEditingGroup(group);
      setGroupLabel(group.label);
      setSelectedGroupUserIds(group.users?.map(u => u.id.toString()) || []);
    } else {
      setEditingGroup(null);
      setGroupLabel("");
      setSelectedGroupUserIds([]);
    }
    setUserSearchTerm("");
    setIsGroupDialogOpen(true);
  };

  const handleSaveGroup = async () => {
    if (!groupLabel.trim()) {
      toast.error("Group label is required");
      return;
    }

    setIsSavingGroup(true);
    try {
      const payload = {
        label: groupLabel.trim(),
        userIds: selectedGroupUserIds
      };

      if (editingGroup) {
        const response = await api.put(`/api/qualifications/groups/${editingGroup.id}`, payload, { skipErrorToast: true });
        if (response.success) {
          setGroups(prev => prev.map(g => g.id === editingGroup.id ? response.group : g));
          toast.success("Group updated successfully");
        }
      } else {
        const response = await api.post("/api/qualifications/groups", payload, { skipErrorToast: true });
        if (response.success) {
          setGroups(prev => [response.group, ...prev]);
          toast.success("Group created successfully");
        }
      }
      setIsGroupDialogOpen(false);
    } catch (error) {
      console.error("Save group error:", error);
      toast.error("Failed to save group");
    } finally {
      setIsSavingGroup(false);
    }
  };

  const toggleArchiveGroup = async (group: QualificationGroup) => {
    try {
      const response = await api.put(`/api/qualifications/groups/${group.id}`, {
        isArchived: !group.isArchived
      }, { skipErrorToast: true });
      if (response.success) {
        setGroups(prev => prev.map(g => g.id === group.id ? response.group : g));
        toast.success(group.isArchived ? "Group restored" : "Group archived");
      }
    } catch (error) {
      console.error("Archive group error:", error);
      toast.error("Failed to update group status");
    }
  };

  const handleOpenTypeDialog = (type?: QualificationType) => {
    if (type) {
      setEditingType(type);
      setTypeLabel(type.label);
    } else {
      setEditingType(null);
      setTypeLabel("");
    }
    setIsTypeDialogOpen(true);
  };

  const handleSaveType = async () => {
    if (!typeLabel.trim()) {
      toast.error("Category label is required");
      return;
    }

    setIsSavingType(true);
    try {
      const payload = { label: typeLabel.trim() };

      if (editingType) {
        const response = await api.put(`/api/qualifications/types/${editingType.id}`, payload, { skipErrorToast: true });
        if (response.success) {
          setTypes(prev => prev.map(t => t.id === editingType.id ? response.type : t));
          toast.success("Category updated successfully");
        }
      } else {
        const response = await api.post("/api/qualifications/types", payload, { skipErrorToast: true });
        if (response.success) {
          setTypes(prev => [response.type, ...prev]);
          toast.success("Category created successfully");
        }
      }
      setIsTypeDialogOpen(false);
    } catch (error) {
      console.error("Save type error:", error);
      toast.error("Failed to save category");
    } finally {
      setIsSavingType(false);
    }
  };

  const toggleArchiveType = async (type: QualificationType) => {
    try {
      const response = await api.put(`/api/qualifications/types/${type.id}`, {
        isArchived: !type.isArchived
      }, { skipErrorToast: true });
      if (response.success) {
        setTypes(prev => prev.map(t => t.id === type.id ? response.type : t));
        toast.success(type.isArchived ? "Category restored" : "Category archived");
      }
    } catch (error) {
      console.error("Archive type error:", error);
      toast.error("Failed to update status");
    }
  };

  const handleOpenPresetDialog = (preset?: any) => {
    if (preset) {
      setEditingPreset(preset);
      setPresetName(preset.name);
      setPresetTypeId(preset.type?.id || "none");
      setPresetAuthority(preset.issuingAuthority || "");
    } else {
      setEditingPreset(null);
      setPresetName("");
      setPresetTypeId("none");
      setPresetAuthority("");
    }
    setIsPresetDialogOpen(true);
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      toast.error("Preset name is required");
      return;
    }

    setIsSavingPreset(true);
    try {
      const payload = {
        name: presetName.trim(),
        typeId: (presetTypeId && presetTypeId !== "none") ? presetTypeId : null,
        issuingAuthority: presetAuthority.trim() || null
      };

      if (editingPreset) {
        const response = await api.put(`/api/qualifications/presets/${editingPreset.id}`, payload, { skipErrorToast: true });
        if (response.success) {
          setPresets(prev => prev.map(p => p.id === editingPreset.id ? response.preset : p));
          toast.success("Preset updated successfully");
        }
      } else {
        const response = await api.post("/api/qualifications/presets", payload, { skipErrorToast: true });
        if (response.success) {
          setPresets(prev => [response.preset, ...prev]);
          toast.success("Preset created successfully");
        }
      }
      setIsPresetDialogOpen(false);
    } catch (error) {
      console.error("Save preset error:", error);
      toast.error("Failed to save preset");
    } finally {
      setIsSavingPreset(false);
    }
  };

  const toggleArchivePreset = async (preset: any) => {
    try {
      const response = await api.put(`/api/qualifications/presets/${preset.id}`, {
        isArchived: !preset.isArchived
      }, { skipErrorToast: true });
      if (response.success) {
        setPresets(prev => prev.map(p => p.id === preset.id ? response.preset : p));
        toast.success(preset.isArchived ? "Preset restored" : "Preset archived");
      }
    } catch (error) {
      console.error("Archive preset error:", error);
      toast.error("Failed to update status");
    }
  };

  const filteredUsers = selectionUsers.filter(u =>
    (u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) || 
     u.email.toLowerCase().includes(userSearchTerm.toLowerCase()))
  );

  const canAccessAny = canReadSettings || canReadDefaults || canReadGroups || canReadTypes || canReadPresets;

  if (!canAccessAny) {
    return (
      <Card className="border-slate-200 shadow-lg p-12 text-center bg-white">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="bg-red-50 p-4 rounded-full">
            <ShieldAlert className="h-12 w-12 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Access Denied</h2>
          <p className="text-slate-500 max-w-md mx-auto">
            You do not have the required permissions to access Qualification Settings. 
            Please contact your system administrator if you believe this is an error.
          </p>
          <Button variant="outline" onClick={() => window.history.back()}>Go Back</Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-full w-full sm:w-fit overflow-x-auto flex flex-nowrap no-scrollbar">
          {canReadPresets && (
            <TabsTrigger value="presets" className="rounded-full px-3 sm:px-4 py-2 data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">
              Presets
            </TabsTrigger>
          )}
          {canReadTypes && (
            <TabsTrigger value="categories" className="rounded-full px-3 sm:px-4 py-2 data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">
              Categories
            </TabsTrigger>
          )}
          {canReadDefaults && (
            <TabsTrigger value="defaults" className="rounded-full px-3 sm:px-4 py-2 data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">
              Defaults
            </TabsTrigger>
          )}
          {canReadGroups && (
            <TabsTrigger value="groups" className="rounded-full px-3 sm:px-4 py-2 data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">
              Groups
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="presets" className="pt-4 space-y-6">
          <Card className="border-slate-200 shadow-sm overflow-hidden bg-white">
            <CardHeader className="border-b">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <CardTitle className="text-xl">Qualification Presets</CardTitle>
                  <CardDescription>Manage standardized templates for common qualifications.</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <input 
                      placeholder="Search presets..." 
                      className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 pl-9 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                      value={presetSearchTerm}
                      onChange={(e) => setPresetSearchTerm(e.target.value)}
                    />
                  </div>
                  {canUpdatePresets && (
                    <Button 
                      onClick={() => handleOpenPresetDialog()} 
                      size="sm"
                      className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm shrink-0"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      New Preset
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-8">
              <Table>
                <TableHeader className="bg-slate-50/80">
                  <TableRow>
                    <TableHead className="w-[120px]">Status</TableHead>
                    <TableHead>Qualification Name</TableHead>
                    <TableHead>Default Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} className="h-24 text-center">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : presets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="h-24 text-center text-slate-500 font-medium">
                        No presets found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    presets
                      .filter(p => p.name.toLowerCase().includes(presetSearchTerm.toLowerCase()))
                      .map((p) => (
                        <TableRow key={p.id} className="hover:bg-slate-50/50 transition-colors">
                          <TableCell>
                            {p.isArchived ? (
                              <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 font-medium">Archived</Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200 font-medium shadow-none">Active</Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-medium text-slate-900">{p.name}</TableCell>
                          <TableCell>
                            {p.type ? <Badge variant="secondary" className="bg-slate-100 text-slate-700 hover:bg-slate-100 font-medium border-slate-200">{p.type.label}</Badge> : "-"}
                          </TableCell>
                          <TableCell className="text-right px-6">
                            {canUpdatePresets && (
                              <div className="flex justify-end gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8 text-slate-400 hover:text-slate-900"
                                  onClick={() => handleOpenPresetDialog(p)}
                                >
                                  <Edit size={16} />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className={`h-8 w-8 ${p.isArchived ? "text-emerald-500 hover:text-emerald-700" : "text-slate-400 hover:text-red-600"}`}
                                  onClick={() => toggleArchivePreset(p)}
                                >
                                  {p.isArchived ? <RotateCcw size={16} /> : <Archive size={16} />}
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="categories" className="pt-4 space-y-6">
          <Card className="border-slate-200 shadow-sm overflow-hidden bg-white">
            <CardHeader className="border-b">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <CardTitle className="text-xl">Qualification Categories</CardTitle>
                  <CardDescription>Manage the preset list used for categorizing qualifications in the Add form.</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <input 
                      placeholder="Search categories..." 
                      className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 pl-9 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                      value={categorySearchTerm}
                      onChange={(e) => setCategorySearchTerm(e.target.value)}
                    />
                  </div>
                  {canUpdateTypes && (
                    <Button 
                      onClick={() => handleOpenTypeDialog()} 
                      size="sm"
                      className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm shrink-0"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      New Category
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-8">
              <Table>
                <TableHeader className="bg-slate-50/80">
                  <TableRow>
                    <TableHead className="w-[120px]">Status</TableHead>
                    <TableHead>Category Label</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={3} className="h-24 text-center">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : types.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="h-24 text-center text-slate-500 font-medium">
                        No qualification categories found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    types
                      .filter(type => type.label.toLowerCase().includes(categorySearchTerm.toLowerCase()))
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((type) => (
                        <TableRow key={type.id} className="hover:bg-slate-50/50 transition-colors">
                          <TableCell>
                            {type.isArchived ? (
                              <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 font-medium">Archived</Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200 font-medium shadow-none">Active</Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-medium text-slate-900">{type.label}</TableCell>
                          <TableCell className="text-right px-6">
                            {canUpdateTypes && (
                              <div className="flex justify-end gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8 text-slate-400 hover:text-slate-900"
                                  onClick={() => handleOpenTypeDialog(type)}
                                >
                                  <Edit size={16} />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className={`h-8 w-8 ${type.isArchived ? "text-emerald-500 hover:text-emerald-700" : "text-slate-400 hover:text-red-600"}`}
                                  onClick={() => toggleArchiveType(type)}
                                >
                                  {type.isArchived ? <RotateCcw size={16} /> : <Archive size={16} />}
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="defaults" className="pt-4 space-y-4">
          <Card className="border-slate-200 shadow-sm overflow-hidden bg-white">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-xl">Global Notification Defaults</CardTitle>
                  <CardDescription>Select default staff members to be notified for all new qualification renewals.</CardDescription>
                </div>
                {canUpdateDefaults && (
                  <Button 
                    onClick={handleSaveDefaults} 
                    disabled={isSavingDefaults || !defaults}
                    size="sm"
                    className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm"
                  >
                    {isSavingDefaults ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save Defaults
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-4 bg-blue-50/50 border border-blue-100 rounded-lg text-blue-800">
                  <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold">System Configuration</p>
                    <p className="text-xs leading-relaxed text-blue-700/80">These recipients and schedules will be pre-selected for <strong>all new</strong> qualifications. You can still modify them per record in the add/edit dialog.</p>
                  </div>
                </div>

                {/* Default Intervals Section */}
                <div className="space-y-4 pb-6 border-b border-slate-100">
                  <Label className="text-sm font-semibold flex items-center text-slate-700">
                    <Bell className="h-4 w-4 mr-2 text-slate-400" />
                    Default Reminder Schedule
                    <span className="ml-2 text-xs font-normal text-slate-500">(Days before expiry)</span>
                  </Label>
                  
                  <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200 min-h-[52px]">
                    {defaults?.defaultIntervals?.map((days) => (
                      <div key={days} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-md shadow-sm">
                        <span className="text-sm font-medium text-slate-700">{days} days</span>
                        <button 
                          type="button"
                          disabled={!canUpdateDefaults}
                          onClick={() => {
                            if (!defaults) return;
                            const next = defaults.defaultIntervals.filter(d => d !== days);
                            setDefaults({ ...defaults, defaultIntervals: next });
                          }}
                          className={`text-slate-400 transition-colors ${canUpdateDefaults ? "hover:text-slate-600" : "cursor-not-allowed opacity-50"}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    {(!defaults?.defaultIntervals || defaults.defaultIntervals.length === 0) && (
                      <span className="text-sm text-slate-400 self-center px-1 italic">No default intervals configured</span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-[200px]">
                      <Input 
                        placeholder="Add custom days" 
                        type="number"
                        className="h-10 bg-white border-slate-200 focus-visible:ring-slate-900"
                        value={defaultIntervalInput}
                        disabled={!canUpdateDefaults}
                        onChange={(e) => setDefaultIntervalInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = parseInt(defaultIntervalInput);
                            if (!isNaN(val) && val > 0 && !defaults?.defaultIntervals.includes(val)) {
                              if (!defaults) return;
                              const next = [...defaults.defaultIntervals, val].sort((a, b) => b - a);
                              setDefaults({ ...defaults, defaultIntervals: next });
                              setDefaultIntervalInput("");
                            }
                          }
                        }}
                      />
                    </div>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      disabled={!canUpdateDefaults}
                      className="h-10 px-4 border-slate-200 hover:bg-slate-50 font-medium"
                      onClick={() => {
                        const val = parseInt(defaultIntervalInput);
                        if (!isNaN(val) && val > 0 && !defaults?.defaultIntervals.includes(val)) {
                          if (!defaults) return;
                          const next = [...defaults.defaultIntervals, val].sort((a, b) => b - a);
                          setDefaults({ ...defaults, defaultIntervals: next });
                          setDefaultIntervalInput("");
                        }
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Interval
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <Label className="text-sm font-semibold flex items-center text-slate-700">
                      Default Recipients
                      <span className="ml-2 text-xs font-normal text-slate-500">({defaults?.defaultRecipients.length || 0} selected)</span>
                    </Label>
                    <div className="relative w-full sm:w-64">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                      <input 
                        placeholder="Search staff..." 
                        className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 pl-9 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                        value={defaultsSearchTerm}
                        onChange={(e) => setDefaultsSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 max-h-[350px] overflow-y-auto p-4 border border-slate-200 rounded-xl bg-slate-50 shadow-inner no-scrollbar">
                    {selectionUsers.filter(u => 
                      (u.name.toLowerCase().includes(defaultsSearchTerm.toLowerCase()) || 
                       u.jobRole.toLowerCase().includes(defaultsSearchTerm.toLowerCase()))
                    ).map((user) => (
                      <div 
                        key={user.id} 
                        className="flex items-center space-x-3 p-3 bg-white border border-slate-100 rounded-lg hover:shadow-sm transition-all cursor-pointer hover:border-slate-300"
                        onClick={() => {
                          if (!defaults || !canUpdateDefaults) return;
                          const current = defaults.defaultRecipients || [];
                          const isChecked = current.includes(user.id.toString());
                          const next = !isChecked 
                            ? (current.includes(user.id.toString()) ? current : [...current, user.id.toString()])
                            : current.filter(id => id !== user.id.toString());
                          setDefaults({ ...defaults, defaultRecipients: next });
                        }}
                      >
                        <Checkbox 
                          id={`default-user-${user.id}`}
                          checked={defaults?.defaultRecipients.includes(user.id.toString())}
                          disabled={!canUpdateDefaults}
                          className="data-[state=checked]:bg-slate-900 border-slate-300 pointer-events-none"
                        />
                        <div className="flex flex-col min-w-0 flex-1">
                          <Label htmlFor={`default-user-${user.id}`} className="text-sm font-medium truncate cursor-pointer text-slate-900 pointer-events-none">{user.name}</Label>
                          <span className="text-[11px] text-slate-500 truncate pointer-events-none">{user.jobRole}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="groups" className="pt-4 space-y-6">
          <Card className="border-slate-200 shadow-sm overflow-hidden bg-white">
            <CardHeader className="border-b">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <CardTitle className="text-xl">Qualification Groups</CardTitle>
                  <CardDescription>Create groups to streamline reporting and mass notification.</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <Input 
                      placeholder="Search groups..." 
                      className="pl-9 h-9 bg-white border-slate-200 focus-visible:ring-slate-900"
                      value={groupSearchTerm}
                      onChange={(e) => setGroupSearchTerm(e.target.value)}
                    />
                  </div>
                  {canUpdateGroups && (
                    <Button 
                      onClick={() => handleOpenGroupDialog()} 
                      size="sm"
                      className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm shrink-0"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      New Group
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-8">
              <Table>
                <TableHeader className="bg-slate-50/80">
                  <TableRow>
                    <TableHead className="w-[120px]">Status</TableHead>
                    <TableHead>Group Name</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} className="h-32 text-center">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : groups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="h-48 text-center text-slate-500">
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <Users className="h-10 w-10 text-slate-200" />
                          <p className="font-medium">No groups created yet</p>
                          <Button variant="outline" size="sm" onClick={() => handleOpenGroupDialog()}>Create your first group</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    groups
                      .filter(g => g.label.toLowerCase().includes(groupSearchTerm.toLowerCase()))
                      .map((group) => (
                      <TableRow key={group.id} className="hover:bg-slate-50/50 transition-colors">
                        <TableCell>
                          {group.isArchived ? (
                            <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200">Archived</Badge>
                          ) : (
                            <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 border-indigo-200 shadow-none">Active</Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-semibold text-slate-900">{group.label}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-600 font-medium">{group.users?.length || 0} users</span>
                            {group.users && group.users.length > 0 && (
                               <div className="flex -space-x-2">
                                  {group.users.slice(0, 3).map((u, i) => (
                                    <div key={i} className="h-6 w-6 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-[10px] font-bold text-slate-600 shadow-sm" title={u.name || "Unknown"}>
                                      {(u.name || "?").charAt(0)}
                                    </div>
                                  ))}
                                  {group.users.length > 3 && (
                                    <div className="h-6 w-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[8px] font-bold text-slate-400 shadow-sm">
                                      +{group.users.length - 3}
                                    </div>
                                  )}
                               </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right px-6">
                          {canUpdateGroups && (
                            <div className="flex justify-end gap-2">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 text-slate-400 hover:text-slate-900"
                                onClick={() => handleOpenGroupDialog(group)}
                              >
                                <Edit size={16} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className={`h-8 w-8 ${group.isArchived ? "text-emerald-500 hover:text-emerald-700" : "text-slate-400 hover:text-red-600"}`}
                                onClick={() => toggleArchiveGroup(group)}
                              >
                                {group.isArchived ? <RotateCcw size={16} /> : <Archive size={16} />}
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={isTypeDialogOpen} onOpenChange={setIsTypeDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{editingType ? "Edit Category" : "Add New Category"}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="type-label">Category Name</Label>
              <Input
                id="type-label"
                value={typeLabel}
                onChange={(e) => setTypeLabel(e.target.value)}
                placeholder="e.g. License, Accreditation, Certificate"
                className="focus-visible:ring-slate-900"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSaveType} disabled={isSavingType} className="bg-slate-900 hover:bg-slate-800 text-white">
              {isSavingType ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {editingType ? "Update Category" : "Create Category"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Dialog */}
      <Dialog open={isGroupDialogOpen} onOpenChange={setIsGroupDialogOpen}>
        <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden border-none shadow-2xl">
          <DialogHeader className="bg-slate-900 text-white p-6">
            <DialogTitle className="text-xl flex items-center gap-3 font-bold">
              <Users className="h-5 w-5" />
              {editingGroup ? "Edit Qualification Group" : "New Qualification Group"}
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 space-y-6 bg-white">
            <div className="space-y-2">
              <Label htmlFor="group-label" className="text-slate-700 font-semibold">Group Name</Label>
              <Input
                id="group-label"
                value={groupLabel}
                onChange={(e) => setGroupLabel(e.target.value)}
                placeholder="e.g. HSE Team, Office Staff"
                className="focus-visible:ring-slate-900 border-slate-200 h-10 shadow-sm"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between font-semibold text-slate-700">
                <Label>Assign Members</Label>
                <span className="text-xs font-normal text-slate-500">{selectedGroupUserIds.length} selected</span>
              </div>
              
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input 
                  placeholder="Search staff members..." 
                  className="pl-9 h-10 bg-slate-50 border-slate-200 mb-3"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 gap-2 max-h-[300px] overflow-y-auto p-2 border border-slate-100 rounded-lg bg-slate-50/50 no-scrollbar shadow-inner">
                {filteredUsers.length === 0 ? (
                  <p className="text-center py-8 text-sm text-slate-400 italic">No users found</p>
                ) : (
                  filteredUsers.map((user) => (
                    <div 
                      key={user.id} 
                      className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                        selectedGroupUserIds.includes(user.id.toString()) 
                          ? "bg-slate-900 text-white border-slate-900 shadow-md transform scale-[1.01]" 
                          : "bg-white text-slate-700 border-slate-100 hover:border-slate-300 hover:shadow-sm"
                      }`}
                      onClick={() => {
                        const isSelected = selectedGroupUserIds.includes(user.id.toString());
                        if (isSelected) {
                          setSelectedGroupUserIds(prev => prev.filter(id => id !== user.id.toString()));
                        } else {
                          setSelectedGroupUserIds(prev => prev.includes(user.id.toString()) ? prev : [...prev, user.id.toString()]);
                        }
                      }}
                    >
                      <Checkbox 
                        id={`group-user-${user.id}`}
                        checked={selectedGroupUserIds.includes(user.id.toString())}
                        className="data-[state=checked]:bg-white data-[state=checked]:text-slate-900 border-slate-300 pointer-events-none"
                      />
                      <div className="flex flex-col min-w-0 flex-1">
                        <Label htmlFor={`group-user-${user.id}`} className="text-sm font-bold truncate cursor-pointer pointer-events-none">{user.name}</Label>
                        <span className={`text-[10px] truncate pointer-events-none ${selectedGroupUserIds.includes(user.id.toString()) ? "text-slate-300" : "text-slate-400"}`}>
                          {user.jobRole} • {(user.department && typeof user.department === 'object') ? user.department.departmentName : (user.department || 'No Department')}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="bg-slate-50 p-6 pt-4">
            <DialogClose asChild>
              <Button variant="ghost" className="hover:bg-slate-200 text-slate-600">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSaveGroup} disabled={isSavingGroup} className="bg-slate-900 hover:bg-slate-800 text-white shadow-lg min-w-[120px]">
              {isSavingGroup ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {editingGroup ? "Update Group" : "Create Group"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preset Dialog */}
      <Dialog open={isPresetDialogOpen} onOpenChange={setIsPresetDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{editingPreset ? "Edit Preset" : "Add New Preset"}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="preset-name">Qualification Name</Label>
              <Input
                id="preset-name"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="e.g. First Aid Level 2"
                className="focus-visible:ring-slate-900"
              />
            </div>
            <div className="space-y-2">
              <Label>Default Category</Label>
              <Select value={presetTypeId} onValueChange={setPresetTypeId}>
                <SelectTrigger className="bg-slate-50 border-slate-200">
                  <SelectValue placeholder="Select optional category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {types.filter(t => !t.isArchived).map(t => (
                    <SelectItem key={t.id} value={t.id.toString()}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="preset-authority">Standard Issuing Authority</Label>
              <Input
                id="preset-authority"
                value={presetAuthority}
                onChange={(e) => setPresetAuthority(e.target.value)}
                placeholder="e.g. Red Cross"
                className="focus-visible:ring-slate-900"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSavePreset} disabled={isSavingPreset} className="bg-slate-900 hover:bg-slate-800 text-white">
              {isSavingPreset ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {editingPreset ? "Update Preset" : "Create Preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
